import React, { useState, useEffect } from 'react';
import { X, Building, CreditCard, Hash, AlertCircle, CheckCircle, User, Loader2, Shield, MapPin } from 'lucide-react';

const BankDetailsPopup = ({ 
  isOpen, 
  onClose, 
  onSave, 
  selectedConnectionId,
  getBankDetails, 
  updateBankDetails,
  bankValidationService // Add this prop for bank validation
}) => {
  const [bankDetails, setBankDetails] = useState({
    account_holder: '',
    bank_name: '',
    sort_code: '',
    account_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Bank validation state
  const [bankValidation, setBankValidation] = useState({
    sortCode: { isValid: null, loading: false, data: null, error: null },
    accountNumber: { isValid: null, loading: false, data: null, error: null },
    combinedValidation: { isValid: null, loading: false, data: null, error: null }
  });
  
  const [validationDebounce, setValidationDebounce] = useState(null);

  // Check if bank details exist when popup opens
  useEffect(() => {
    const checkBankDetails = async () => {
      if (isOpen && selectedConnectionId) {
        setLoading(true);
        try {
          const response = await getBankDetails(selectedConnectionId);
          if (response?.data) {
            const { account_holder, bank_name, sort_code, account_number } = response.data;
            
            // Pre-fill any existing data
            setBankDetails({
              account_holder: account_holder || '',
              bank_name: bank_name || '',
              sort_code: sort_code || '',
              account_number: account_number || ''
            });
          }
        } catch (error) {
          console.error('Failed to fetch bank details:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    checkBankDetails();
  }, [isOpen, selectedConnectionId, getBankDetails]);

  // Bank validation functions
  const validateSortCode = (sortCode) => {
    if (!sortCode) return { isValid: false, message: "Sort code is required" };
    
    const cleanSortCode = sortCode.toString().replace(/\D/g, '');
    
    if (cleanSortCode.length !== 6) {
      return { isValid: false, message: "Sort code must be 6 digits" };
    }
    
    if (!/^\d{6}$/.test(cleanSortCode)) {
      return { isValid: false, message: "Sort code must contain only numbers" };
    }
    
    return { isValid: true, message: "" };
  };

  const validateAccountNumber = (accountNumber) => {
    if (!accountNumber) return { isValid: false, message: "Account number is required" };
    
    const cleanAccountNumber = accountNumber.toString().replace(/\D/g, '');
    
    if (cleanAccountNumber.length < 6 || cleanAccountNumber.length > 8) {
      return { isValid: false, message: "Account number must be between 6-8 digits" };
    }
    
    if (!/^\d+$/.test(cleanAccountNumber)) {
      return { isValid: false, message: "Account number must contain only numbers" };
    }
    
    return { isValid: true, message: "" };
  };

  const formatSortCode = (value) => {
    if (!value) return '';
    
    const cleanSortCode = value.replace(/\D/g, '');
    
    if (cleanSortCode.length === 6) {
      return cleanSortCode.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3');
    }
    
    return cleanSortCode;
  };

  const formatAccountNumber = (value) => {
    return value ? value.replace(/\D/g, '').slice(0, 8) : '';
  };

  // Bank validation API call
  const validateBankAccountWithAPI = async (sortCode, accountNumber) => {
    const cleanSortCode = (sortCode || '').toString().replace(/\D/g, '');
    const cleanAccountNumber = (accountNumber || '').toString().replace(/\D/g, '');

    // Basic validation first
    if (cleanSortCode.length !== 6 || cleanAccountNumber.length < 6 || cleanAccountNumber.length > 8) {
      return;
    }

    setBankValidation(prev => ({
      ...prev,
      combinedValidation: { ...prev.combinedValidation, loading: true, error: null }
    }));

    try {
      const response = await bankValidationService.validateBankAccount(
        cleanSortCode, 
        cleanAccountNumber
      );

      setBankValidation(prev => ({
        ...prev,
        combinedValidation: {
          isValid: response.valid,
          loading: false,
          data: response.data,
          error: response.error
        }
      }));

      // Auto-fill bank name if validation is successful and bank name is available
      if (response.valid && response.data) {
        const bankName = response.data.bank || response.data.bankname;
        if (bankName && !bankDetails.bank_name) {
          setBankDetails(prev => ({
            ...prev,
            bank_name: bankName
          }));
        }
      }

    } catch (error) {
      setBankValidation(prev => ({
        ...prev,
        combinedValidation: {
          isValid: false,
          loading: false,
          data: null,
          error: error.message || 'Validation service unavailable'
        }
      }));
    }
  };

  // Debounced validation effect
  useEffect(() => {
    if (validationDebounce) {
      clearTimeout(validationDebounce);
    }

    const cleanSortCode = (bankDetails.sort_code || '').toString().replace(/\D/g, '');
    const cleanAccountNumber = (bankDetails.account_number || '').toString().replace(/\D/g, '');

    if (cleanSortCode.length === 6 && cleanAccountNumber.length >= 6 && cleanAccountNumber.length <= 8) {
      const timer = setTimeout(() => {
        validateBankAccountWithAPI(cleanSortCode, cleanAccountNumber);
      }, 1000);

      setValidationDebounce(timer);
    }

    return () => {
      if (validationDebounce) {
        clearTimeout(validationDebounce);
      }
    };
  }, [bankDetails.sort_code, bankDetails.account_number]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!bankDetails.account_holder.trim()) {
      newErrors.account_holder = 'Account holder name is required';
    }

    if (!bankDetails.bank_name.trim()) {
      newErrors.bank_name = 'Bank name is required';
    }
    
    // Use the enhanced validation functions
    const sortCodeValidation = validateSortCode(bankDetails.sort_code);
    if (!sortCodeValidation.isValid) {
      newErrors.sort_code = sortCodeValidation.message;
    }

    const accountNumberValidation = validateAccountNumber(bankDetails.account_number);
    if (!accountNumberValidation.isValid) {
      newErrors.account_number = accountNumberValidation.message;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSortCodeChange = (value) => {
    const rawValue = value ? value.replace(/\D/g, '') : '';
    
    // Basic validation
    const validation = validateSortCode(rawValue);
    if (!validation.isValid && rawValue) {
      setErrors(prev => ({ ...prev, sort_code: validation.message }));
    } else if (errors.sort_code) {
      setErrors(prev => ({ ...prev, sort_code: "" }));
    }
    
    // Format for display
    const formattedValue = formatSortCode(value || '');
    setBankDetails(prev => ({ ...prev, sort_code: formattedValue }));

    // Reset validation when changing
    setBankValidation(prev => ({
      ...prev,
      combinedValidation: { isValid: null, loading: false, data: null, error: null }
    }));
  };

  const handleAccountNumberChange = (value) => {
    const cleanValue = value ? value.replace(/\D/g, '') : '';
    
    const validation = validateAccountNumber(cleanValue);
    if (!validation.isValid && cleanValue) {
      setErrors(prev => ({ ...prev, account_number: validation.message }));
    } else if (errors.account_number) {
      setErrors(prev => ({ ...prev, account_number: "" }));
    }
    
    setBankDetails(prev => ({ ...prev, account_number: cleanValue }));

    // Reset validation when changing
    setBankValidation(prev => ({
      ...prev,
      combinedValidation: { isValid: null, loading: false, data: null, error: null }
    }));
  };

  const handleInputChange = (field, value) => {
    if (field === 'sort_code') {
      handleSortCodeChange(value);
    } else if (field === 'account_number') {
      handleAccountNumberChange(value);
    } else {
      setBankDetails(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const formattedDetails = {
        ...bankDetails,
        sort_code: (bankDetails.sort_code || '').toString().replace(/\D/g, ''),
        account_number: (bankDetails.account_number || '').toString().replace(/\D/g, '')
      };
      
      await updateBankDetails(selectedConnectionId, formattedDetails);
      onSave(formattedDetails);
      onClose();
    } catch (error) {
      console.error('Failed to save bank details:', error);
      setErrors({ submit: 'Failed to save bank details. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Building size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bank Details Required</h2>
              <p className="text-blue-100 text-sm">Please provide your banking information</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Checking existing details...</span>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Account Holder Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User size={16} className="inline mr-2" />
                  Account Holder Name
                </label>
                <input
                  type="text"
                  value={bankDetails.account_holder}
                  onChange={(e) => handleInputChange('account_holder', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.account_holder 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  placeholder="e.g., John Doe"
                />
                {errors.account_holder && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.account_holder}
                  </p>
                )}
              </div>

              {/* Bank Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Building size={16} className="inline mr-2" />
                  Bank Name
                </label>
                <input
                  type="text"
                  value={bankDetails.bank_name}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.bank_name 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  placeholder="e.g., Barclays, HSBC, Lloyds"
                />
                {errors.bank_name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.bank_name}
                  </p>
                )}
              </div>

              {/* Sort Code */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Hash size={16} className="inline mr-2" />
                  Sort Code *
                </label>
                <input
                  type="text"
                  value={bankDetails.sort_code}
                  onChange={(e) => handleInputChange('sort_code', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.sort_code
                      ? 'border-red-300 focus:border-red-500'
                      : bankValidation.combinedValidation.isValid === false
                      ? 'border-red-500'
                      : bankValidation.combinedValidation.isValid === true
                      ? 'border-green-500'
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  placeholder="12-34-56"
                  maxLength={8}
                />
                
                {/* Enhanced Validation Messages for Sort Code */}
                {errors.sort_code && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.sort_code}
                  </p>
                )}
                
                {bankValidation.combinedValidation.loading && (
                  <p className="text-blue-600 text-sm mt-1 flex items-center">
                    <Loader2 size={14} className="mr-1 animate-spin" />
                    Validating bank account...
                  </p>
                )}
                
                {bankValidation.combinedValidation.isValid === true && (
                  <div className="space-y-1 mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                      <CheckCircle size={14} />
                      Bank Account Validated Successfully
                    </p>
                    {bankValidation.combinedValidation.data && (
                      <div className="text-green-600 text-sm space-y-1 ml-6">
                        {bankValidation.combinedValidation.data.bank && (
                          <p className="flex items-center gap-2">
                            <Building size={12} />
                            <span className="font-medium">Bank:</span> {bankValidation.combinedValidation.data.bank}
                          </p>
                        )}
                        {bankValidation.combinedValidation.data.bankname && (
                          <p className="flex items-center gap-2">
                            <Building size={12} />
                            <span className="font-medium">Bank:</span> {bankValidation.combinedValidation.data.bankname}
                          </p>
                        )}
                        {bankValidation.combinedValidation.data.bic && (
                          <p className="flex items-center gap-2">
                            <Shield size={12} />
                            <span className="font-medium">BIC:</span> {bankValidation.combinedValidation.data.bic}
                          </p>
                        )}
                        {bankValidation.combinedValidation.data.branch && (
                          <p className="flex items-center gap-2">
                            <MapPin size={12} />
                            <span className="font-medium">Branch:</span> {bankValidation.combinedValidation.data.branch}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {bankValidation.combinedValidation.isValid === false && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm font-medium flex items-center gap-2">
                      <AlertCircle size={14} />
                      Invalid Bank Account Details
                    </p>
                    {bankValidation.combinedValidation.error && (
                      <p className="text-red-600 text-sm mt-1 ml-6">
                        {bankValidation.combinedValidation.error}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <CreditCard size={16} className="inline mr-2" />
                  Account Number *
                </label>
                <input
                  type="text"
                  value={bankDetails.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    errors.account_number
                      ? 'border-red-300 focus:border-red-500'
                      : bankValidation.combinedValidation.isValid === false
                      ? 'border-red-500'
                      : bankValidation.combinedValidation.isValid === true
                      ? 'border-green-500'
                      : 'border-gray-200 focus:border-blue-500'
                  }`}
                  placeholder="12345678"
                  maxLength={8}
                />
                
                {/* Enhanced Validation Messages for Account Number */}
                {errors.account_number && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.account_number}
                  </p>
                )}
                
                {!errors.account_number && !bankValidation.combinedValidation.loading && 
                 bankValidation.combinedValidation.isValid === null && bankDetails.account_number && (
                  <p className="text-green-600 text-sm mt-1 flex items-center">
                    <CheckCircle size={14} className="mr-1" />
                    Valid account number format
                  </p>
                )}
              </div>

              {/* Bank Validation Summary */}
              {bankValidation.combinedValidation.isValid === true && (
                <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0">
                      <CheckCircle size={20} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-green-800">
                        Bank Account Successfully Validated
                      </h3>
                      <p className="text-green-600 text-xs">
                        All banking details have been verified and are ready for use.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm flex items-center">
                    <AlertCircle size={14} className="mr-2" />
                    {errors.submit}
                  </p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Details'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankDetailsPopup;